import { useCallback, useEffect, useMemo, useState } from "react"

import { Feed, Texts } from "@doktor-se/bones-ui/dist/web-shared/components"
import { usePrevious } from "@doktor-se/bones-ui/dist/web-shared/hooks"
import { AssignedConversation, ProfileMessage } from "@doktor-se/bones-ui/dist/web-shared/types"
import { PhrasesVariableOpts } from "@doktor-se/bones-ui/dist/web-shared/utils"

import {
  answerCall,
  checkPermissions,
  createLoadingSelector,
  deleteMessage,
  endCall,
  fetchAssignedConversation,
  fetchSignedUrl,
  lastMessageRead,
  patientProfilesFetch,
  sendActionButton,
  sendComment,
  sendMessage,
  uploadFile,
  webdocUpload
} from "api"
import iAmTyping from "api/ws/handlers/handleTyping"
import { appStatic, featureFlags, phrasesLanguages, webdocFeatureEnabled } from "config"
import { AmplitudeEventMap } from "lib/amplitude/events"
import { useSelectedConversationAmplitudeLogger } from "lib/amplitude/hooks"
import { useAppDispatch, useAppSelector } from "lib/hooks"
import { enterSend } from "reducers/app"
import { selectBookingById, selectBookingTypeById, selectVisitTypeById } from "reducers/booking"
import { categorySelectors } from "reducers/categories"
import { clinicSelectors } from "reducers/clinics"
import { selectLocalConversationState, updateLocalConversationState } from "reducers/selected"
import { patientProfilesSelectors } from "reducers/users/users.reducer"

import SmsReminderButton from "components/ConversationContainer/SmsReminderButton/SmsReminderButton"

import PatientInfoTabs from "./PatientInfoTabs"

import styles from "./FeedContainer.module.scss"

interface FeedContainerProps {
  conversation: AssignedConversation
  texts: Texts
}

const webdocLoadingSelector = createLoadingSelector(["WEBDOC"])
const fileUploadLoadingSelector = createLoadingSelector(["FILE UPLOAD"])

const FeedContainer = ({ conversation, texts }: FeedContainerProps) => {
  const dispatch = useAppDispatch()
  const [messagesToDisplayFiltered, setMessagesToDisplayFiltered] = useState([
    ...(conversation?.messagesToDisplay || [])
  ])
  const defaultRole = useAppSelector(state => state.auth.defaultRole)

  const localConversationState = useAppSelector(selectLocalConversationState)
  const staff = useAppSelector(state => state.users.staff)
  const enterState = useAppSelector(state => state.app.enterState)
  const clinics = useAppSelector(clinicSelectors.selectAll)
  const { phrases } = useAppSelector(state => state.phrases)
  const loaderForWebdocUpload = useAppSelector(state => webdocLoadingSelector(state))

  const loaderForFileUpload = useAppSelector(state => fileUploadLoadingSelector(state))
  const categories = useAppSelector(categorySelectors.selectEntities)
  const user = useAppSelector(state => state.auth.user)
  const patientProfiles = useAppSelector(
    state => conversation && patientProfilesSelectors.selectById(state, conversation.patientId)
  )?.profiles
  const sealdEncryptionFeatureEnabled = false

  const logEvent = useSelectedConversationAmplitudeLogger()

  const logAmplitudeEvent = (eventType: string) => {
    const type = eventType as keyof AmplitudeEventMap
    logEvent(type)
  }
  const prevMessages = usePrevious(conversation?.messagesToDisplay)

  useEffect(() => {
    if (featureFlags.has("conversations_load_split") && conversation?.id && !conversation?.messages) {
      dispatch(fetchAssignedConversation(conversation.id))
    }
    if (conversation?.messagesToDisplay === prevMessages) return

    let newMessages = conversation?.messagesToDisplay || []

    setMessagesToDisplayFiltered(newMessages)
  }, [conversation, messagesToDisplayFiltered, setMessagesToDisplayFiltered, prevMessages, dispatch, user])

  useEffect(() => {
    if (!conversation || !!patientProfiles || !conversation.messages) return
    const profileMessages = conversation.messages.filter(message => message.type === "profile") as ProfileMessage[]
    const hasOldProfileMessage = profileMessages.find(message => message.data.newProfileId !== conversation.profile?.id)
    if (hasOldProfileMessage) dispatch(patientProfilesFetch(conversation?.patientId))
  }, [conversation, dispatch, patientProfiles])

  const fetchSignedUrlCallback = useCallback(
    (key: string, conversationId: string) => {
      return dispatch(fetchSignedUrl(key, conversationId))
    },
    [dispatch]
  )

  const userClinicName = clinics.find(c => c.id === user?.data?.clinic)?.name

  const booking = useAppSelector(selectBookingById(conversation?.metadata?.bookingId))
  const kindId = booking?.kindId
  const bookingType = useAppSelector(selectBookingTypeById(kindId))
  const visitType = useAppSelector(selectVisitTypeById(bookingType?.visitTypeId))

  const isUrgentCareConversation = useMemo(() => !!booking?.clinicId, [booking])
  const isChatEnabled = useMemo(() => visitType?.chatEnabled === true, [visitType?.chatEnabled])
  const conversationIsBooked = useMemo(() => conversation.state === "booked", [conversation.state])
  const conversationIsOpened = useMemo(() => conversation.state === "opened", [conversation.state])
  // Update comment state based on conversation state and chat availability
  if (isUrgentCareConversation && localConversationState?.state.commentState !== true) {
    if (conversationIsBooked) {
      dispatch(updateLocalConversationState({ conversationId: conversation.id, state: { commentState: true } }))
    } else if (conversationIsOpened && !isChatEnabled) {
      dispatch(updateLocalConversationState({ conversationId: conversation.id, state: { commentState: true } }))
    }
  }

  const phrasesVariableOpts: PhrasesVariableOpts = {
    staffFirstName: user?.firstname,
    staffLastName: user?.lastname,
    staffDisplayName: user?.displayName,
    title: user?.title,
    patientFirstName: conversation.account?.firstname,
    patientLastName: conversation.account?.lastname,
    patientGender: conversation.account?.data?.sex,
    staffGender: user?.data?.sex,
    staffClinic: userClinicName,
    staffClinicId: user?.data?.clinic
  }

  if (!user) return null

  return (
    <div className={styles.feedWrapper}>
      <Feed
        className={styles.feedContainer}
        user={user}
        staff={staff}
        conversation={conversation}
        messagesToDisplaySearched={messagesToDisplayFiltered}
        conversationIdForFilteredMessages={conversation.id}
        categories={categories}
        localConversationState={localConversationState}
        phraseData={phrases}
        updateLocalConversationState={state =>
          dispatch(updateLocalConversationState({ conversationId: conversation.id, state }))
        }
        conversationSeen={(conversationId, messageId) => dispatch(lastMessageRead(conversationId, messageId))}
        sendMessage={(conversationId, draft, staffName, patientId) =>
          dispatch(
            sendMessage(
              conversationId,
              draft,
              staffName,
              patientId,
              sealdEncryptionFeatureEnabled,
              conversation?.customerMetadata?.encryption
            )
          )
        }
        sendActionButton={(conversationId: string, title: string, uri: string, event?: string) =>
          dispatch(sendActionButton(conversationId, title, uri, event))
        }
        sendComment={(conversationId, draft) => dispatch(sendComment(conversationId, draft))}
        uploadFile={(file, conversationId, staffName, patientId) =>
          dispatch(
            uploadFile(
              file,
              conversationId,
              staffName,
              patientId,
              sealdEncryptionFeatureEnabled,
              conversation?.customerMetadata?.encryption
            )
          )
        }
        enterSend={() => dispatch(enterSend())}
        enterState={enterState}
        endCall={() => dispatch(endCall(conversation, "declined"))}
        answerCall={() => dispatch(checkPermissions(() => dispatch(answerCall(conversation, "answered"))))}
        deleteMessage={messageId => dispatch(deleteMessage(messageId, conversation))}
        iAmTyping={() => iAmTyping(conversation.id)}
        loaderForWebdocUpload={loaderForWebdocUpload}
        loaderForFileUpload={loaderForFileUpload}
        features={featureFlags}
        webdocFeatureEnabled={webdocFeatureEnabled(defaultRole)}
        uploadImage={(url, profile) => dispatch(webdocUpload(url, profile))}
        texts={texts}
        phrasesLanguages={phrasesLanguages}
        fetchSignedUrl={fetchSignedUrlCallback}
        patientProfiles={patientProfiles}
        appStatic={appStatic}
        phrasesVariableOpts={phrasesVariableOpts}
        amplitudeLoggerCallback={logAmplitudeEvent}
        smsReminderComponent={<SmsReminderButton conversation={conversation} />}
        disableFooterCommentToggle={
          isUrgentCareConversation && (conversationIsBooked || (conversationIsOpened && !isChatEnabled))
        }
        formSummaryVersion="v2"
      />

      <PatientInfoTabs />
    </div>
  )
}

export default FeedContainer
